/** Recorte de RFC 7807 usado para classificar 401/403 no cliente. */
export type ProblemSnippet = {
  type?: string;
  code?: string;
  detail?: string;
};

function haystack(problem: ProblemSnippet): {
  type: string;
  code: string;
  detail: string;
} {
  return {
    type: (problem.type ?? "").toLowerCase(),
    code: (problem.code ?? "").toUpperCase(),
    detail: (problem.detail ?? "").toLowerCase(),
  };
}

/**
 * 401 de negócio/autorização: a sessão continua válida.
 * Access token sem `custom:tenant_id` cai aqui (`tenant-missing`).
 */
export function isNonSession401(problem: ProblemSnippet | null): boolean {
  if (!problem) return false;
  const { type, code } = haystack(problem);
  if (type.includes("tenant-missing") || code === "TENANT_MISSING") return true;
  if (type.includes("permission-denied") || code === "PERMISSION_DENIED") return true;
  if (type.includes("permission-undeclared") || code === "PERMISSION_UNDECLARED")
    return true;
  return false;
}

/**
 * 401 claramente de JWT ausente/expirado/inválido — candidato a refresh.
 * Corpo vazio (gateway/WAF) é tratado como JWT.
 */
export function isJwtUnauthorizedProblem(problem: ProblemSnippet | null): boolean {
  if (isNonSession401(problem)) return false;
  if (!problem) return true;
  const { type, detail } = haystack(problem);
  if (type.includes("unauthorized") || type.includes("unauthenticated")) return true;
  return /token expirado|token inválido|bearer token|falha na validação do token/.test(
    detail,
  );
}

export function shouldAttemptSessionRefresh(
  status: number,
  problem: ProblemSnippet | null,
): boolean {
  return status === 401 && isJwtUnauthorizedProblem(problem);
}

/**
 * Expira a sessão só se o refresh falhou, ou se após refresh o 401 ainda for JWT.
 * 403 e tenant-missing nunca matam a sessão.
 */
export function shouldDeclareSessionLost(opts: {
  status: number;
  problem: ProblemSnippet | null;
  refreshAttempted: boolean;
  refreshSucceeded: boolean;
}): boolean {
  if (opts.status === 403) return false;
  if (opts.refreshAttempted && !opts.refreshSucceeded) return true;
  if (
    opts.status === 401 &&
    opts.refreshSucceeded &&
    isJwtUnauthorizedProblem(opts.problem)
  ) {
    return true;
  }
  return false;
}
